import dayjs from 'dayjs';
import { FieldType, IntermediaryDraftDto, IntermediaryFcaData, IntermediaryResponse, UserRoleType, UserStatus } from '@pmp/api';
import { DATE_FORMAT } from '@pmp/utils';

import styles from './styles.module.css';

const toDateOnly = (value: unknown): string | null => {
  if (!value) return null;
  const d = dayjs(value as string);
  return d.isValid() ? d.format(DATE_FORMAT) : null;
};

export const mapIntermediaryToDto = (values: IntermediaryResponse, record?: IntermediaryResponse | null): IntermediaryDraftDto => {
  const { personalData, compliance } = values;
  const roles = values.roles ?? [];
  return {
    id: values.id ?? record?.id ?? null,
    firmId: values.firmId ?? null,
    email: values.email ?? null,
    title: personalData?.title ?? null,
    firstName: personalData?.firstName ?? null,
    lastName: personalData?.lastName ?? null,
    birthDate: toDateOnly(personalData?.birthDate),
    mobilePhoneNumber: personalData?.mobilePhoneNumber ?? null,
    workPhoneNumber: personalData?.workPhoneNumber ?? null,
    previousName: personalData?.previousName ?? null,
    lastComplianceCheck: compliance?.lastComplianceCheck ?? null,
    whoDidLastComplianceCheckUserId: compliance?.whoDidLastComplianceCheckUserId ?? null,
    complianceStatus: compliance?.complianceStatus ?? null,
    onTheWatchlist: compliance?.onTheWatchlist ?? null,
    roles: values.roles ?? null,
    managerId: values.managerId ?? null,
    subordinateIds: values.subordinateIds ?? null,
    assistants: roles.includes(UserRoleType.ADVISOR) ? values.assistants ?? null : [],
    advisers: roles.includes(UserRoleType.ASSISTANT) ? values.advisers ?? null : [],
    tradingAddressId: values.tradingAddressId ?? record?.tradingAddressId ?? null,
    irn: values.irn ?? null,
    fcaStatus: values.fcaStatus ?? null,
    receiveMarketingMaterials: values.receiveMarketingMaterials ?? null,
    agreedToTermsAndConditions: values.agreedToTermsAndConditions ?? null,
    isAuthorised: values.isAuthorised ?? null,
    userStatus: typeof values.userStatus === 'boolean'
      ? (values.userStatus ? UserStatus.ACTIVE : UserStatus.INACTIVE)
      : (values.userStatus ?? null),
  };
};

export const getBrokerHighlightProps = (
  dataIndex: string | string[],
  fcaModifiedFields: IntermediaryFcaData | null | undefined,
): string => {
  if (!fcaModifiedFields) return '';
  const path = Array.isArray(dataIndex) ? dataIndex : [dataIndex];
  if (path[0] === 'irn') {
    return 'irn' in (fcaModifiedFields.registrationDetails ?? {}) ? styles.highlightedField : '';
  }
  if (path[0] === 'fcaStatus') {
    return 'fcaStatus' in (fcaModifiedFields.registrationDetails ?? {}) ? styles.highlightedField : '';
  }
  if (path[0] === 'personalData' && path[1]) {
    return path[1] in (fcaModifiedFields.personalDetails ?? {}) ? styles.highlightedField : '';
  }
  return '';
};

export const BROKERS_VALUE_LIST_NAMES: FieldType[] = [
  FieldType.YES_NO,
  FieldType.COMPLIANCE_STATUS,
  FieldType.USER_STATUS,
  FieldType.USER_ROLE_TYPE,
  FieldType.ASSISTANT_PERMISSION_TYPE,
  FieldType.FIRMS,
  FieldType.TITLES,
];

export const ALLOWED_ROLE_COMBINATIONS: UserRoleType[][] = [
  [UserRoleType.ADVISOR, UserRoleType.SUPERVISOR],
  [UserRoleType.ASSISTANT, UserRoleType.SUPERVISOR],
];

export const isRoleOptionDisabled = (role: UserRoleType, selected: UserRoleType[]): boolean => {
  if (selected.includes(role)) return false;
  if (selected.length === 0) return false;
  const candidate = [...selected, role];
  return !ALLOWED_ROLE_COMBINATIONS.some(combo => candidate.every(r => combo.includes(r)));
};
